<template>
  <div class="multiselect" :style="fontSize ? { '--custom-font-size': fontSize } : {}" :data-validation="dataValidation" v-click-outside="closeDropdown">
    <div
      v-if="!hideSelectBox"
      ref="selectBox"
      class="select-box show-error-bdr"
      :class="{ readonly: readonly }"
      @click="!readonly && !useExternalTrigger && toggleDropdown()"
    >
      <span v-if="selectedItems.length === 0" class="placeholder">
        {{ placeholder }}
      </span>

      <span v-for="item in selectedItems" :key="item.id" class="tag">
        {{ item.name }}
        <span v-if="!readonly" class="remove" @click.stop="removeItem(item.id)">&times;</span>
      </span>

      <span v-if="!readonly" class="arrow">&#9662;</span>
    </div>

    <div
      v-if="useExternalTrigger"
      ref="trigger"
      class="multiselect__trigger"
      @click.stop="!readonly && toggleDropdown()"
    >
      <slot name="trigger" />
    </div>

    <div
      v-if="isOpen"
      ref="dropdown"
      class="dropdown"
      :class="[`open-${openDirection}`, { 'no-search': !enableSearch, 'dropdown--fixed': useExternalTrigger }]"
      :style="dropdownStyle"
    >
      <div v-if="enableSearch" class="dropdown-search">
        <input
          ref="searchInput"
          v-model.trim="searchText"
          type="text"
          placeholder="Search..."
          @click.stop
        >
      </div>

      <div v-if="filteredOptions.length" class="dropdown-options">
        <label v-for="option in filteredOptions" :key="option.id" class="option">
          <input
            type="checkbox"
            :disabled="option.disabled"
            :value="option.id"
            v-model="internalValue"
            @click.stop="select(option)"
          >
          <span class="checkbox" :class="{ disabled: option.disabled }"></span>
          <span :class="{ disabled: option.disabled }">{{ option.name }}</span>
        </label>
      </div>

      <div v-else class="empty-state">No options found</div>
    </div>
  </div>
</template>

<script>
import { removeErrorBorder } from '@/helper/formValidation/errorBorder'

export default {
  name: 'CustomMultiSelectDropdown',

  props: {
    options: {
      type: Array,
      required: true
    },
    value: {
      type: Array,
      default: () => []
    },
    selected: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Select options'
    },
    readonly: {
      type: Boolean,
      default: false
    },
    enableSearch: {
      type: Boolean,
      default: false
    },
    dataValidation: {
      type: String,
      default: ''
    },
    useExternalTrigger: {
      type: Boolean,
      default: false
    },
    hideSelectBox: {
      type: Boolean,
      default: false
    },
    dropdownMinWidth: {
      type: Number,
      default: 220
    },
    alignRight: {
      type: Boolean,
      default: false
    },
    fontSize: {
      type: String,
      default: 'var(--text-s)'
    }
  },

  data () {
    return {
      isOpen: false,
      internalValue: [...(this.selected.length ? this.selected : this.value)],
      searchText: '',
      openDirection: 'down',
      dropdownStyle: {}
    }
  },

  computed: {
    selectedItems () {
      return this.options.filter((opt) => this.internalValue.includes(opt.id))
    },
    filteredOptions () {
      if (!this.enableSearch || !this.searchText) return this.options
      const searchKeyword = this.searchText.toLowerCase()
      return this.options.filter((option) => {
        const name = option.name ? option.name.toLowerCase() : ''
        const id = option.id ? String(option.id).toLowerCase() : ''
        return name.includes(searchKeyword) || id.includes(searchKeyword)
      })
    }
  },

  watch: {
    selected (newVal) {
      if (JSON.stringify(newVal) !== JSON.stringify(this.internalValue)) {
        this.internalValue = [...newVal]
      }
    },
    value (newVal) {
      if (JSON.stringify(newVal) !== JSON.stringify(this.internalValue)) {
        this.internalValue = [...newVal]
      }
    },
    internalValue (newVal) {
      this.$emit('input', newVal)
    }
  },

  methods: {
    toggleDropdown () {
      if (!this.isOpen) {
        this.$root.$emit('close-all-multiselects')
      }

      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.$nextTick(() => {
          this.updateDropdownDirection()
          window.addEventListener('resize', this.updateDropdownDirection)
          window.addEventListener('scroll', this.updateDropdownDirection, true)
          if (this.enableSearch && this.$refs.searchInput) this.$refs.searchInput.focus()
        })
      } else {
        this.cleanupDropdownState()
      }
    },
    closeDropdown () {
      this.isOpen = false
      this.cleanupDropdownState()
    },
    removeItem (id) {
      this.internalValue = this.internalValue.filter((i) => i !== id)
      this.$emit('deselect', id)
    },
    select () {
      if (this.dataValidation) {
        removeErrorBorder([this.dataValidation])
      }
    },
    getTriggerEl () {
      if (this.useExternalTrigger) return this.$refs.trigger
      return this.$refs.selectBox
    },
    updateDropdownDirection () {
      const triggerEl = this.getTriggerEl()
      const dropdownEl = this.$refs.dropdown
      if (!triggerEl) return

      const triggerRect = triggerEl.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const dropdownHeight = dropdownEl?.offsetHeight || 260
      const spaceBelow = viewportHeight - triggerRect.bottom
      const spaceAbove = triggerRect.top

      if (this.useExternalTrigger) {
        const width = Math.max(this.dropdownMinWidth, triggerRect.width)
        let left = triggerRect.left

        if (this.alignRight) {
          left = triggerRect.right - width
        }

        if (left + width > window.innerWidth) {
          left = Math.max(8, window.innerWidth - width - 8)
        }

        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
          this.openDirection = 'up'
          this.dropdownStyle = {
            position: 'fixed',
            left: `${left}px`,
            top: `${Math.max(8, triggerRect.top - dropdownHeight - 6)}px`,
            width: `${width}px`,
            zIndex: 1000
          }
        } else {
          this.openDirection = 'down'
          this.dropdownStyle = {
            position: 'fixed',
            left: `${left}px`,
            top: `${triggerRect.bottom + 6}px`,
            width: `${width}px`,
            zIndex: 1000
          }
        }
      } else {
        this.dropdownStyle = {}
        this.openDirection = spaceBelow < dropdownHeight && spaceAbove > spaceBelow ? 'up' : 'down'
      }
    },
    cleanupDropdownState () {
      if (this.enableSearch) this.searchText = ''
      this.dropdownStyle = {}
      window.removeEventListener('resize', this.updateDropdownDirection)
      window.removeEventListener('scroll', this.updateDropdownDirection, true)
    },
    handleCloseAll () {
      this.isOpen = false
      this.cleanupDropdownState()
    }
  },
  mounted () {
    this.$root.$on('close-all-multiselects', this.handleCloseAll)
  },
  beforeDestroy () {
    this.$root.$off('close-all-multiselects', this.handleCloseAll)
    this.cleanupDropdownState()
  },

  directives: {
    clickOutside: {
      bind (el, binding) {
        el.__ClickOutsideHandler__ = (event) => {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value()
          }
        }
        document.addEventListener('click', el.__ClickOutsideHandler__)
      },
      unbind (el) {
        document.removeEventListener('click', el.__ClickOutsideHandler__)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.multiselect {
  position: relative;
  width: 100%;
  font-family: inherit;

  .disabled {
    color: #94a3b8;
  }

  .select-box {
    min-height: 44px;
    border: 1px solid #cbd5e1;
    padding: 10px 36px 10px 12px;
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 6px;
    cursor: pointer;
    border-radius: 0.285em;
    background: transparent;
    font-size: var(--custom-font-size, var(--text-base));
    color: #1f2937;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;

    &:hover {
      border-color: #94a3b8;
      box-shadow: 0 0 0 1px var(--brand-color-1);
    }

    &:focus-within {
      border-color: #3b82f6;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.15);
    }

    &.readonly {
      cursor: default;
      background-color: #f9fafb;
      border-color: #e5e7eb;

      &:hover {
        border-color: #e5e7eb;
        box-shadow: none;
      }
    }
  }

  .placeholder {
    color: #94a3b8;
    font-size: var(--custom-font-size, var(--text-base));
  }

  .tag {
    background: var(--brand-light-color);
    padding: 4px 10px;
    border-radius: 6px;
    font-size: var(--custom-font-size, var(--text-s));
    display: flex;
    align-items: center;
    gap: 6px;
    line-height: 1;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    .remove {
      cursor: pointer;
      font-weight: 600;
      flex-shrink: 0;
      color: #c75e5e;
    }
  }

  .arrow {
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: var(--custom-font-size, var(--text-s));
    color: #64748b;
  }

  .dropdown {
    position: absolute;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    background: #fff;
    border: 1px solid #cbd5e1;
    border-radius: 10px;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.08);
    max-height: 260px;
    overflow: hidden;
    z-index: 100;

    &--fixed {
      left: auto;
      right: auto;
      top: auto;
      bottom: auto;
    }

    &.open-down:not(.dropdown--fixed) {
      top: calc(100% + 6px);
      bottom: auto;
    }

    &.open-up:not(.dropdown--fixed) {
      top: auto;
      bottom: calc(100% + 6px);
      flex-direction: column-reverse;
    }

    .dropdown-search {
      position: sticky;
      top: 0;
      z-index: 1;
      padding: 8px;
      border-bottom: 1px solid #e2e8f0;
      background: #fff;

      input {
        width: 100%;
        height: 34px;
        border: 1px solid #cbd5e1;
        border-radius: 6px;
        padding: 0 10px;
        font-size: var(--custom-font-size, var(--text-s));
        color: #1f2937;
        outline: none;
      }
    }

    .dropdown-options {
      flex: 1;
      max-height: 202px;
      overflow-y: auto;
    }

    .empty-state {
      padding: 12px;
      font-size: var(--custom-font-size, var(--text-s));
      color: #64748b;
      text-align: center;
    }

    .option {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 6px 12px;
      font-size: var(--custom-font-size, var(--text-s));
      line-height: 1.2;
      cursor: pointer;
      color: #1f2937;

      input {
        display: none;
      }

      .checkbox {
        width: 13px;
        height: 13px;
        border: 1px solid #f3ca52;
        border-radius: 3px;
        position: relative;
        flex-shrink: 0;
      }

      input:checked + .checkbox {
        background: var(--brand-color);
      }

      input:checked + .checkbox::after {
        content: "";
        position: absolute;
        left: 3px;
        top: 0;
        width: 4px;
        height: 8px;
        border: solid #fff;
        border-width: 0 2px 2px 0;
        transform: rotate(45deg);
      }

      .checkbox.disabled {
        border-color: #94a3b8;
        background: #e5e7eb;
      }

      input:checked + .checkbox.disabled {
        background: #94a3b8;
      }
    }

    &.open-up .dropdown-search {
      top: auto;
      bottom: 0;
      border-bottom: none;
      border-top: 1px solid #e2e8f0;
    }

    &.no-search .dropdown-options {
      max-height: 258px;
    }
  }
}
</style>
